import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { isEmpty, noop, startCase } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  ActionModalControlPanel,
  type ActionModalControlPanelProps,
} from '@revelio/core';
import {
  SelectionCategories,
  type SelectionList,
  type SelectionListIdNames,
  type TreeItem,
} from '@revelio/iso-utility';

import { BreadcrumbTreeSelectionWithControls } from '../../../../../../shared/components/tree/breadcrumb-tree/BreadcrumbTreeSelectionWithControls';
import { useSelectionLists } from '../../../../../../shared/selection-lists/hooks/useSelectionLists';
import { SelectionCategoryLabelOverrides } from '../../../../../../shared/selection-lists/selection-category-label-overrides';
import type { GqlSelectionListKey } from '../../../../../../shared/selection-lists/types';
import type { ReportSelectionCategories } from '../utils';
import type { ReportEntityCompanyEditorHandle } from './report-entity-company-editor';
import { ReportEntityCompanySelections } from './report-entity-company-selections';
import {
  GEOGRAPHY_GRANULARITY_FILTERS_WITH_STATE,
  isACompanySelectionList,
  isGeographyGranularityWithState,
} from './report-entity-selections-legacy-modal-helpers';

export interface ReportEntitySelectionsModalProps
  extends Partial<ActionModalControlPanelProps> {
  headerLabel?: string;
  selectionLists?: ReportSelectionCategories[];
  isOpen: boolean;
  submitHandler?: (selections: Record<string, TreeItem<string>>) => void;
  closeOnSubmit?: boolean;
  initialSelections?: Record<string, TreeItem>;
  limit?: number;
}

export const ReportEntitySelectionsLegacyModal = ({
  headerLabel = '',
  selectionLists = [],
  isOpen,
  onClose = noop,
  onCancel,
  submitHandler,
  submitText,
  closeOnSubmit = true,
  initialSelections = {},
  limit,
}: ReportEntitySelectionsModalProps) => {
  const [selections, setSelections] =
    useState<Record<string, TreeItem>>(initialSelections);

  const [numBreadcrumbTreeSelections, setNumBreadcrumbTreeSelections] =
    useState<number>(0);

  const searchInputRef = useRef(null);
  const treeRef = useRef<{
    submit: () => void;
    handleClearSelections: () => void;
  }>();
  const companyEditorRef = useRef<ReportEntityCompanyEditorHandle>(null);
  const shouldUseReportEntityCompanyEditor = selectionLists?.some((list) =>
    isACompanySelectionList(list)
  );
  const normalizedSelectionLists = selectionLists.map((s) =>
    isACompanySelectionList(s) ? SelectionCategories.COMPANY : s
  );
  const isGeoWithState = isGeographyGranularityWithState(selectionLists);

  // BE return the filters with state after metro area, this is a quick fix to maintain correct order
  const sortedListIds = [...GEOGRAPHY_GRANULARITY_FILTERS_WITH_STATE];
  const sortedGeoSelectionListIds = sortedListIds.filter((s) =>
    normalizedSelectionLists.includes(s as SelectionCategories)
  );

  // Fetch selection lists for breadcrumb tree
  const s = useSelectionLists(
    isGeoWithState ? (sortedGeoSelectionListIds as GqlSelectionListKey[]) : []
  );

  const breadcrumbSelectionLists = useMemo(() => {
    return Object.entries(s || {}).map(([key, value]) => ({
      id: key as SelectionListIdNames,
      label: key as SelectionListIdNames,
      value,
    })) as SelectionList[];
  }, [s]);

  const handleReset = () => {
    treeRef.current?.handleClearSelections?.();
    companyEditorRef.current?.handleClearSelections();
  };

  const handleSubmit = () => {
    submitHandler?.(selections);
    setSelections({});

    if (closeOnSubmit) {
      onClose();
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setSelections(initialSelections);
  }, [isOpen]);

  const handleBreadcrumbTreeSubmit = (
    breadCrumbTreeSelections: Record<string, TreeItem>
  ) => {
    const selectionsWithoutGeo = Object.entries(selections)
      .filter(
        ([_key, item]) =>
          !sortedGeoSelectionListIds.includes(
            item.selectionListId as SelectionCategories
          )
      )
      .reduce<Record<string, TreeItem>>((acc, [key, item]) => {
        acc[key] = item;
        return acc;
      }, {});

    const newSelections = {
      ...selectionsWithoutGeo,
      ...breadCrumbTreeSelections,
    };
    setSelections(newSelections);
    submitHandler?.(newSelections);
    if (closeOnSubmit) {
      onClose();
    }
  };

  const showSelected = (limit || 0) === 1;

  const header = (
    <Flex alignItems="center">
      <Text>{`Select ${headerLabel}`}</Text>
      {showSelected && (
        <Text
          fontSize={14}
          color="gray.600"
          ml={2}
        >{`(${isGeoWithState ? numBreadcrumbTreeSelections : Object.values(selections).length || 0}/${limit} Selected)`}</Text>
      )}
    </Flex>
  );

  const renderContent = () => {
    if (shouldUseReportEntityCompanyEditor) {
      return (
        <ModalBody py="16px">
          <ReportEntityCompanySelections
            selectionLists={selectionLists}
            treeHeight={315}
            ref={companyEditorRef}
            limit={limit ?? 1}
            onSelections={setSelections}
          />
        </ModalBody>
      );
    }

    if (isGeoWithState) {
      return (
        <BreadcrumbTreeSelectionWithControls
          selectionListIds={sortedGeoSelectionListIds}
          initialSelections={selections}
          branches={sortedGeoSelectionListIds.map(
            (listName: SelectionCategories) =>
              SelectionCategoryLabelOverrides[listName] ?? startCase(listName)
          )}
          selectionLists={breadcrumbSelectionLists}
          maxSelections={limit}
          onSubmit={handleBreadcrumbTreeSubmit}
          onClose={onClose}
          setNumSelections={setNumBreadcrumbTreeSelections}
          renderWrapper={({ tree, controls }) => {
            return (
              <>
                <ModalBody py="16px">{tree}</ModalBody>
                <ModalFooter width="100%" borderTop="1px solid #E5EBF1">
                  {controls}
                </ModalFooter>
              </>
            );
          }}
        />
      );
    }

    return (
      <ModalBody py="16px">
        <Text>Invalid report configuration</Text>
      </ModalBody>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      initialFocusRef={searchInputRef}
    >
      <ModalOverlay />
      <ModalContent minHeight="530px">
        <ModalHeader
          borderBottom="1px solid #E5EBF1"
          fontSize="17px"
          fontWeight="600"
          py="12px"
        >
          {header}
        </ModalHeader>
        <ModalCloseButton size="sm" />
        {renderContent()}
        {!isGeoWithState && (
          <ModalFooter borderTop="1px solid #E5EBF1">
            <ActionModalControlPanel
              submitText={submitText}
              onClose={onClose}
              onCancel={onCancel}
              onSubmit={handleSubmit}
              onReset={handleReset}
              submitIsDisabled={isEmpty(selections)}
            />
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};
